import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Loader2,
  Mail,
  KeyRound,
  ShieldCheck,
  ArrowLeft,
  MessageCircle,
  Smartphone,
} from 'lucide-react';

const STEPS = { EMAIL: 1, CODE: 2, PASSWORD: 3, DONE: 4 };

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) {
      toast.error('Informe seu e-mail');
      return;
    }
    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim().toLowerCase(), 'email');
      toast.success('Se este e-mail estiver cadastrado, enviaremos um código em instantes.');
      setStep(STEPS.CODE);
      setResendCooldown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar o código');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    if (e) e.preventDefault();
    if (code.length !== 6) {
      toast.error('Digite os 6 dígitos do código');
      return;
    }
    setLoading(true);
    try {
      await authAPI.verifyResetCode(email.trim().toLowerCase(), code);
      toast.success('Código verificado. Defina sua nova senha.');
      setStep(STEPS.PASSWORD);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    if (e) e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('A senha deve ter no mínimo 8 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(email.trim().toLowerCase(), code, newPassword);
      setStep(STEPS.DONE);
      toast.success('Senha redefinida com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível redefinir a senha');
    } finally {
      setLoading(false);
    }
  };

  const StepHeader = () => {
    const titles = {
      [STEPS.EMAIL]: { icon: Mail, t: 'Esqueci minha senha', d: 'Informe o e-mail cadastrado para receber o código de verificação.' },
      [STEPS.CODE]: { icon: KeyRound, t: 'Verificar código', d: `Digite o código de 6 dígitos enviado para ${email}` },
      [STEPS.PASSWORD]: { icon: ShieldCheck, t: 'Definir nova senha', d: 'Escolha uma senha forte com no mínimo 8 caracteres, uma maiúscula e um número.' },
      [STEPS.DONE]: { icon: ShieldCheck, t: 'Senha redefinida!', d: 'Sua senha foi alterada com sucesso. Faça login para continuar.' },
    };
    const cfg = titles[step];
    const Icon = cfg.icon;
    return (
      <CardHeader className="space-y-2 pb-6">
        <div className="mx-auto w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
          <Icon className="w-6 h-6 text-blue-600" />
        </div>
        <CardTitle className="text-2xl font-bold text-center">{cfg.t}</CardTitle>
        <CardDescription className="text-center">{cfg.d}</CardDescription>
      </CardHeader>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-slate-900">
            DESTACK <span className="text-blue-600">Vendas+</span>
          </h1>
          <p className="text-sm text-slate-500 mt-1">Recuperação de senha</p>
        </div>

        <Card className="border-0 shadow-xl">
          <StepHeader />
          <CardContent>
            {step === STEPS.EMAIL && (
              <form onSubmit={requestCode} className="space-y-4" data-testid="step-email-form">
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail cadastrado</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="send-code-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Enviar código por e-mail
                </Button>

                <div className="pt-2">
                  <div className="text-xs text-slate-500 mb-2 text-center">Outros canais (em breve)</div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button type="button" variant="outline" disabled className="text-xs" data-testid="channel-whatsapp">
                      <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
                    </Button>
                    <Button type="button" variant="outline" disabled className="text-xs" data-testid="channel-sms">
                      <Smartphone className="w-3.5 h-3.5 mr-1" /> SMS
                    </Button>
                  </div>
                </div>
              </form>
            )}

            {step === STEPS.CODE && (
              <form onSubmit={verifyCode} className="space-y-4" data-testid="step-code-form">
                <div className="space-y-2">
                  <Label htmlFor="code">Código de verificação</Label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    autoFocus
                    className="text-center text-2xl tracking-[0.6em] font-mono"
                    data-testid="forgot-code-input"
                  />
                  <p className="text-xs text-slate-500">O código expira em 15 minutos.</p>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading || code.length !== 6}
                  data-testid="verify-code-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  Verificar código
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    className="text-slate-500 hover:text-slate-700 flex items-center gap-1"
                    onClick={() => { setStep(STEPS.EMAIL); setCode(''); }}
                    data-testid="back-to-email-btn"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Trocar e-mail
                  </button>
                  <button
                    type="button"
                    className="text-blue-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                    onClick={requestCode}
                    disabled={loading || resendCooldown > 0}
                    data-testid="resend-code-btn"
                  >
                    {resendCooldown > 0 ? `Reenviar em ${resendCooldown}s` : 'Reenviar código'}
                  </button>
                </div>
              </form>
            )}

            {step === STEPS.PASSWORD && (
              <form onSubmit={submitNewPassword} className="space-y-4" data-testid="step-password-form">
                <div className="space-y-2">
                  <Label htmlFor="new_password">Nova senha</Label>
                  <Input
                    id="new_password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirmar nova senha</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="reset-password-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                  Redefinir senha
                </Button>
              </form>
            )}

            {step === STEPS.DONE && (
              <div className="text-center space-y-4" data-testid="step-done">
                <div className="mx-auto w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-emerald-600" />
                </div>
                <p className="text-slate-600 text-sm">
                  Sua senha foi atualizada com segurança. Você já pode acessar o painel com a nova senha.
                </p>
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={() => navigate('/login')}
                  data-testid="go-to-login-btn"
                >
                  Ir para o login
                </Button>
              </div>
            )}

            {step !== STEPS.DONE && (
              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Voltar para o login
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="mt-8 text-center text-sm text-slate-500">
          DESTACK Publicidade e Marketing Digital
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
